import { useLocation, useNavigate, useParams } from "react-router-dom";
import addressApi from "api/landlord/address";
import { useState, useEffect } from "react";
import { getErrorMessage } from "utils/error";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { Plus } from "untitledui-js-base";
import { Layout } from "providers/Layout";
import { getShortAddressFromAddress } from "shared/addressUtils";
import { DeletePropertyModal } from "shared/app/DeletePropertyModal";
import { useAuthContext } from "providers/Auth";
import { UnitsTableView } from "shared/app/UnitsTableView/index";
import { ApplicationsTableView } from "shared/app/ApplicationsTableView/index";
import { Loader } from "shared/components/Loader/index";
import { Tabs } from "shared/components/Tabs/index";
import { Property } from "types/landlord/address";
import { Redirect } from "shared/components/Redirect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { PropertyKind } from "shared/types";
import { DiscountCodesTab } from "../properties-id-units-id/components/DiscountCodesTab";
import { useSettingsController } from "../settings-integration/controller";
import { LayoutHeader } from "./components/LayoutHeader";
import { LayoutFooter } from "./components/LayoutFooter";
import { PropertyDetails } from "./components/PropertyDetails";
import { LeadsTableView } from "shared/app/LeadsTableView";

const getTabs = (
  type: ReturnType<typeof useAuthContext>["type"],
  propertyKind: PropertyKind | null,
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>
) => {
  const tabs: string[] = [`${dictionary.Property} details`];

  if (propertyKind !== PropertyKind.SingleFamily) tabs.push(dictionary.Units);

  if (type.isBoomCRM) tabs.push("Leads");

  if (type.isBoomScreen) tabs.push("Applications");

  tabs.push("Discount codes");

  return tabs;
};

export const PropertyDetail = () => {
  const location = useLocation();
  const { isEverySyncEnabled } = useSettingsController();
  const dictionary = useUnitsPropertiesDictionary();
  const { type } = useAuthContext();
  const queryClient = useQueryClient();
  const { propertyId: propertyIdParam } = useParams();
  const navigate = useNavigate();

  // MobileView
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(false);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState(false);
  const [propertyId, setpropertyId] = useState<string | null>(null);

  const propertyMeta = useQuery({
    queryKey: ["properties", propertyIdParam],
    queryFn: () => addressApi.getProperty(propertyIdParam || "")
  });

  const property: Property | undefined = propertyMeta.data;

  const { defaultTab } = location.state || {};

  const [tab, setActiveTab] = useState(
    defaultTab || getTabs(type, propertyMeta.data?.kind, dictionary)[0]
  );

  useEffect(() => {
    if (propertyMeta.isSuccess && propertyMeta.data?.id)
      setpropertyId(propertyMeta.data.id);
  }, [propertyMeta.isSuccess, propertyMeta.data]);

  const { mutate, isPending } = useMutation({
    mutationKey: ["turnOnOffLinks", propertyId],
    mutationFn: (active: boolean) => {
      return addressApi.updateAllLinks(propertyId || "", active);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/portal/v1/properties/${propertyId}/units/by_name`]
      });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  if (type.isSDK && !type.isBoomScreen) return <Redirect href="/" />;

  if (propertyMeta.isLoading)
    return (
      <Layout>
        <div className="flex items-center justify-center w-full h-full">
          <Loader />
        </div>
      </Layout>
    );

  if (!property || propertyMeta.isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading {dictionary.property}.
          </h1>
          <p className="text-base font-medium">{propertyMeta.error?.message}</p>
        </div>
      </Layout>
    );

  const propertyName =
    property.name || getShortAddressFromAddress(property.address) || "";
  const tabs = getTabs(type, property.kind, dictionary);

  const componentsMap = {
    [`${dictionary.Property} details`]: () => (
      <PropertyDetails property={property} />
    ),
    [dictionary.Units]: () => (
      <UnitsTableView
        property={property}
        query={`/portal/v1/properties/${propertyId}/units/by_name`}
        propertyId={Number(propertyId)}
        buttons={
          <div className="flex w-full gap-2 md:w-auto">
            <Show if={type.isBoomScreen}>
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.BASE}
                onClick={() => mutate(true)}
                loading={isPending}
                disabled={isEverySyncEnabled}
                tooltip={
                  isEverySyncEnabled
                    ? "The settings has already been automatically synchronized for all partners. (Settings/integration)"
                    : ""
                }
                className="w-full md:w-auto"
              >
                Allow all
              </Button>
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.BASE}
                onClick={() => mutate(false)}
                loading={isPending}
                disabled={isEverySyncEnabled}
                tooltip={
                  isEverySyncEnabled
                    ? "The settings has already been automatically synchronized for all partners. (Settings/integration)"
                    : ""
                }
                className="w-full md:w-auto"
              >
                Disallow all
              </Button>
            </Show>
            {type.isPMS || type.isPropertyManager ? null : (
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.PRIMARY}
                onClick={() =>
                  navigate(`/properties/${propertyId}/units/add`, {
                    state: {
                      property
                    }
                  })
                }
                Icon={Plus}
              >
                Add {dictionary.Units}
              </Button>
            )}
          </div>
        }
      />
    ),
    ["Leads"]: () => (
      <LeadsTableView
        property_ids={propertyIdParam ? [String(propertyIdParam)] : []}
      />
    ),
    ["Applications"]: () => (
      <ApplicationsTableView propertyId={Number(propertyId)} />
    ),
    ["Discount codes"]: () => <DiscountCodesTab />
  };

  return (
    <Layout
      footerClassName="p-4 border-t border-alpha-100"
      footer={
        <LayoutFooter
          onOpenInviteModal={() => setShowInviteApplicantsModal(true)}
          property={property}
        />
      }
      hideFooterForDesktop
      headerClassName="py-0"
      header={
        <LayoutHeader
          propertyName={propertyName}
          property={property}
          setShowInviteApplicantsModal={setShowInviteApplicantsModal}
        />
      }
    >
      <div>
        <Tabs
          selected={tab}
          setActiveTab={(t) => setActiveTab(t)}
          tabs={tabs}
          variant="underline"
        />

        <div className="pt-4">{componentsMap[tab]()}</div>
      </div>

      <DeletePropertyModal
        opened={showDeletePropertyModal}
        onClose={() => setShowDeletePropertyModal(false)}
        propertyId={property.id.toString()}
        onSuccess={() => {
          navigate("/properties");
        }}
      />
      {showInviteApplicantsModal && propertyName && (
        <InviteAndCreateModal
          open={showInviteApplicantsModal}
          setOpen={() => setShowInviteApplicantsModal(false)}
          propertyId={property.id}
          propertyName={propertyName}
          // unitId={undefined}
          onInviteRefreshKey={["properties", propertyId]}
        />
      )}
    </Layout>
  );
};
